import type {
  CostDistribution,
  CostEstimate,
  CostEstimateWorkflowKind,
  CostFallbackInfo,
  CostFallbackLoad,
  CostFallbackSource,
  CostScenarioTotal,
  CostTotalDistribution,
} from '~/features/cost-estimate/types/cost-estimate.types';

import type { CostEstimateRead } from '../types/run-ledger.api.types';

const WORKFLOW_KINDS = new Set<CostEstimateWorkflowKind>([
  'CATALOG_OPERATIONS',
  'EXPERIMENT',
  'SCHEDULED_PIPELINE',
  'SCHEDULED_ENRICHMENT',
]);
const FALLBACK_SOURCES = new Set<CostFallbackSource>([
  'live',
  'quota',
  'fitted',
  'static',
  'historical',
  'mixed',
]);
const FALLBACK_LOADS = new Set<CostFallbackLoad>(['below', 'above', 'overall', 'live']);

/** A number under either spelling: the distributions are the estimator's own JSON, stored raw. */
function num(dist: Record<string, unknown>, camel: string, snake: string): number | null {
  const value = dist[camel] ?? dist[snake];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toDistribution(dist: Record<string, unknown> | null | undefined): CostDistribution | null {
  if (!dist) return null;
  const avgUsd = num(dist, 'avgUsd', 'avg_usd');
  if (avgUsd === null) return null;
  return {
    avgUsd,
    minUsd: num(dist, 'minUsd', 'min_usd') ?? avgUsd,
    p99Usd: num(dist, 'p99Usd', 'p99_usd') ?? avgUsd,
    maxUsd: num(dist, 'maxUsd', 'max_usd') ?? avgUsd,
    lowUsd: num(dist, 'lowUsd', 'low_usd'),
    highUsd: num(dist, 'highUsd', 'high_usd'),
  };
}

function toTotal(dist: Record<string, unknown> | null | undefined): CostTotalDistribution | null {
  const base = toDistribution(dist);
  if (!base || !dist) return base;
  return {
    ...base,
    rangeMargin: num(dist, 'rangeMargin', 'range_margin'),
    rangeConfidence: num(dist, 'rangeConfidence', 'range_confidence'),
  };
}

function toScenario(
  scenario:
    | { avgUsd?: number | null; lowUsd?: number | null; highUsd?: number | null }
    | null
    | undefined,
): CostScenarioTotal | null {
  if (scenario?.avgUsd === undefined || scenario.avgUsd === null) return null;
  return {
    avgUsd: scenario.avgUsd,
    lowUsd: scenario.lowUsd ?? null,
    highUsd: scenario.highUsd ?? null,
  };
}

function toFallback(fallback: CostEstimateRead['fallback']): CostFallbackInfo | null {
  if (!fallback) return null;
  const rates: Record<string, number> = {};
  for (const [key, value] of Object.entries(fallback.rates ?? {})) {
    if (typeof value === 'number') rates[key] = value;
  }
  return {
    source: FALLBACK_SOURCES.has(fallback.source as CostFallbackSource)
      ? (fallback.source as CostFallbackSource)
      : null,
    load: FALLBACK_LOADS.has(fallback.load as CostFallbackLoad)
      ? (fallback.load as CostFallbackLoad)
      : null,
    windowMinutes: fallback.windowMinutes ?? null,
    backlogProducts: fallback.backlogProducts ?? null,
    rates: Object.keys(rates).length > 0 ? rates : null,
  };
}

function toUsd(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * catalog-service's §95 estimate as the shared cost components' `CostEstimate` (the shape they
 * were built on): the same fields, with `connectorCode` as `platform` and the Decimal thresholds
 * as numbers. Only the presentational pieces read it — decisions go to catalog-service.
 */
export function toCostEstimateView(estimate: CostEstimateRead): CostEstimate {
  const kind = estimate.workflowKind as CostEstimateWorkflowKind;
  const scenarios = estimate.scenarios
    ? {
        projected: toScenario(estimate.scenarios.projected),
        geminiOnly: toScenario(estimate.scenarios.geminiOnly),
        fallbackOnly: toScenario(estimate.scenarios.fallbackOnly),
      }
    : null;
  return {
    id: estimate.id,
    workflowId: estimate.workflowId,
    workflowKind: WORKFLOW_KINDS.has(kind) ? kind : 'CATALOG_OPERATIONS',
    platform: estimate.connectorCode === 'META' ? 'META' : 'GMC',
    status: estimate.status,
    productCount: estimate.productCount,
    sampleSize: estimate.sampleSize,
    estimatedProducts: estimate.estimatedProducts,
    perProduct: toDistribution(estimate.perProduct),
    total: toTotal(estimate.total),
    alertThresholdUsd: toUsd(estimate.alertThresholdUsd),
    approvalThresholdUsd: toUsd(estimate.approvalThresholdUsd),
    calibrated: estimate.calibrated ?? false,
    error: estimate.error ?? null,
    decidedBy: estimate.decidedBy ?? null,
    decidedVia: estimate.decidedVia ?? null,
    decidedAt: estimate.decidedAt ?? null,
    expiresAt: estimate.expiresAt ?? null,
    createdAt: estimate.createdAt,
    canDecide: estimate.canDecide,
    approvalTier: estimate.approvalTier ?? null,
    subsidiaryId: estimate.subsidiaryId,
    runLabel: estimate.runLabel ?? null,
    runPath: estimate.runPath ?? null,
    attended: estimate.attended,
    scenarios,
    fallback: toFallback(estimate.fallback),
    approvalMode: estimate.approvalMode ?? null,
  };
}
